import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { HiOutlineSearch, HiOutlineSun, HiOutlineMoon } from 'react-icons/hi';
import { supabase } from '../supabaseClient';
import { useTheme } from '../hooks/useTheme';
import { formatKES } from '../utils/cacheUtils';

const LINKS = [
  { to: '/', label: 'Shop', end: true },
  { to: '/about', label: 'About' },
  { to: '/resume', label: 'Resume' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    supabase.from('products').select('*').then(({ data }) => {
      if (data) setProducts(data);
    });
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen]);

  const results = query.trim()
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.description.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5)
    : [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/?search=${encodeURIComponent(query)}`);
      setSearchOpen(false);
      setQuery('');
    }
  };

  const handleResultClick = (product) => {
    navigate(`/?product=${product.id}`);
    setSearchOpen(false);
    setQuery('');
  };

  return (
    <header className="navbar">
      <NavLink to="/" className="navbar-logo">Jowey's</NavLink>

      <button
        className={`hamburger${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label="Toggle menu"
      >
        <span /><span /><span />
      </button>

      <nav className={`navbar-links${menuOpen ? ' open' : ''}`}>
        {LINKS.map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="navbar-actions">
        <div className="search-wrap" ref={searchRef}>
          <button
            className="icon-btn"
            onClick={() => setSearchOpen((o) => !o)}
            aria-label="Search products"
          >
            <HiOutlineSearch size={18} />
          </button>
          {searchOpen && (
            <div className="search-dropdown">
              <form className="search-form" onSubmit={handleSearch}>
                <input
                  className="search-input"
                  placeholder="Search products…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="btn btn-primary btn-sm">Go</button>
              </form>
              <div className="search-results">
                {query.trim() === '' && (
                  <div className="search-empty">Type to search products</div>
                )}
                {query.trim() !== '' && results.length === 0 && (
                  <div className="search-empty">No products found</div>
                )}
                {results.map((p) => (
                  <div key={p.id} className="search-result" onClick={() => handleResultClick(p)}>
                    <img src={p.image_url || p.imageUrl} alt={p.name} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.name}</div>
                      <div style={{ color: 'var(--accent)', fontSize: '0.82rem' }}>{formatKES(p.price)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="icon-btn" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <HiOutlineSun size={18} /> : <HiOutlineMoon size={18} />}
        </button>
      </div>
    </header>
  );
}
